import { languageCodes } from "@services/file-translations/file-translation-validation/language-detection/languages.constant";
import { getLanguageDistributions } from "@services/file-translations/file-translation-validation/language-detection/trigram-distributions";

/**
 * Builds trigram frequency profile for a text
 * @param text Input text to analyze
 * @returns Map of trigrams to their frequencies
 */
function buildTextProfile(text: string): Map<string, number> {
  const profile = new Map<string, number>();
  const normalizedText = text.toLowerCase().replace(/[0-9\s\p{P}]/gu, ' ');

  // Generate trigrams (3-character sequences)
  for (let i = 0; i < normalizedText.length - 2; i++) {
    const trigram = normalizedText.substring(i, i + 3);
    if (trigram.trim().length === 3) { // Only use trigrams without spaces
      profile.set(trigram, (profile.get(trigram) || 0) + 1);
    }
  }

  return profile;
}

/**
 * Normalizes a frequency profile to sum to 1
 * @param profile Frequency map to normalize
 * @returns Normalized frequency map
 */
function normalizeProfile(profile: Map<string, number>): Map<string, number> {
  const normalized = new Map<string, number>();

  // Calculate total frequency
  const total = Array.from(profile.values()).reduce((sum, freq) => sum + freq, 0);

  // Normalize each frequency
  if (total > 0) {
    for (const [key, value] of profile.entries()) {
      normalized.set(key, value / total);
    }
  }

  return normalized;
}

/**
 * Calculates the similarity between two frequency profiles
 * @param profile1 First frequency profile
 * @param profile2 Second frequency profile
 * @returns Similarity score (higher means more similar)
 */
function calculateProfileSimilarity(profile1: Map<string, number>, profile2: Map<string, number>): number {
  // Cosine similarity calculation
  let dotProduct = 0;
  let magnitude1 = 0;
  let magnitude2 = 0;

  // Calculate dot product
  for (const [key, value1] of profile1.entries()) {
    const value2 = profile2.get(key) || 0;
    dotProduct += value1 * value2;
  }

  // Calculate magnitudes
  for (const value of profile1.values()) {
    magnitude1 += value * value;
  }

  for (const value of profile2.values()) {
    magnitude2 += value * value;
  }

  // Calculate cosine similarity
  magnitude1 = Math.sqrt(magnitude1);
  magnitude2 = Math.sqrt(magnitude2);

  if (magnitude1 > 0 && magnitude2 > 0) {
    return dotProduct / (magnitude1 * magnitude2);
  }

  return 0;
}

/**
 * Calculate percentage of text that matches a script's regex
 * @param text Input text
 * @param scriptRegex Regex for the script
 * @returns Percentage of characters matching the script
 */
function calculateScriptCoverage(text: string, scriptRegex: RegExp): number {
  const matches = text.match(scriptRegex);
  if (!matches) return 0;

  const matchLength = matches.join('').length;
  return (matchLength / text.length) * 100;
}

/**
 * Detects the language of text content using trigram frequency analysis
 * @param text The text content to analyze
 * @returns The detected language information with all matches
 */
export function detectLanguageByTrigram(text: string) {
  // Build profile for the input text
  const textProfile = buildTextProfile(text);
  const normalizedTextProfile = normalizeProfile(textProfile);

  // Get language distributions
  const languageDistributions = getLanguageDistributions();

  // Scores for each language
  const scores: Map<string, number> = new Map();

  // First, filter by script coverage to narrow down candidates
  const scriptCandidates: string[] = [];
  const scriptThreshold = 30; // Require at least 30% coverage

  for (const [language, distribution] of Object.entries(languageDistributions)) {
    const primaryCoverage = calculateScriptCoverage(text, distribution.scriptPrimary);
    let secondaryCoverage = 0;

    if (distribution.scriptSecondary) {
      secondaryCoverage = calculateScriptCoverage(text, distribution.scriptSecondary);
    }

    const totalCoverage = primaryCoverage + secondaryCoverage;
    if (totalCoverage > scriptThreshold) {
      scriptCandidates.push(language);
    }
  }

  // If no candidates, use Latin as fallback (most common script)
  if (scriptCandidates.length === 0) {
    scriptCandidates.push(...Object.keys(languageDistributions).filter(lang =>
      languageDistributions[lang].scriptPrimary.toString().includes('[A-Za-z]')));
  }

  // For each script candidate, calculate similarity score
  for (const language of scriptCandidates) {
    const distribution = languageDistributions[language];
    const similarity = calculateProfileSimilarity(normalizedTextProfile, distribution.trigrams);
    scores.set(language, similarity);
  }

  // Create matches array with all languages that have a score
  const matches = [];
  for (const [language, score] of scores.entries()) {
    // Convert score to confidence percentage (0-100)
    let confidence = Math.min(Math.round(score * 100), 100);

    // For very short texts, reduce confidence
    if (text.length < 100) {
      confidence = Math.round(confidence * 0.8);
    }

    // Only include if confidence is > 0
    if (confidence > 0) {
      matches.push({
        language: language,
        languageCode: languageCodes[language] || 'unknown',
        confidence: confidence
      });
    }
  }

  // Sort matches by confidence (descending)
  matches.sort((a, b) => b.confidence - a.confidence);

  // If no matches were found
  if (matches.length === 0) {
    return {
      primaryLanguage: {
        language: 'unknown',
        languageCode: 'unknown',
        confidence: 0
      },
      allMatches: []
    };
  }

  // Get the top match
  const topMatch = matches[0];

  return {
    primaryLanguage: {
      language: topMatch.language,
      languageCode: topMatch.languageCode,
      confidence: topMatch.confidence
    },
    allMatches: matches
  };
}
